import React, { useEffect } from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import { useParams, useRouter } from "../shared/hooks/useNavigation";

import Footer from "../components/Footer/Footer";
import InteractiveHomepage from "../components/InteractiveHomepage/InteractiveHomepage";
import StaticHomepage from "../components/StaticHomepage/StaticHomepage";
import { useAppState } from "../contexts/ApplicationStateProvider";
import { useStores } from "../contexts/StoreContext";
import Seo from "../shared/components/Seo/Seo";
import getHomepageData from "../shared/helpers/getDataHelpers/getHomepageData";
import getLayoutStaticProps from "../shared/helpers/getDataHelpers/getLayoutStaticProps";
import getFilters from "../shared/helpers/getFilters/getFilters";
import getVacancies from "../shared/helpers/getVacancies/getVacancies";
import { SERVER_SIDE_FETCH_LIMIT } from "../stores/FiltersStore";

function Home(props) {
  const { homepage, vacancies, totalResults, buckets } = props;

  const { isInteractive } = useAppState();
  const { homepageStore, footerStore, filtersStore, vacancyStore } =
    useStores();
  const { setHomepage, hasVideos } = homepageStore;
  const {
    setSearchResults,
    setFilters,
    setResultsPagination,
    setSearchBuckets,
    filtersMapping,
  } = filtersStore;
  const { setSelectedVacancy, setIsVacancyLoading } = vacancyStore;
  const router = useRouter();
  const searchParams = useParams();

  useEffect(() => {
    const load = async () => {
      const queryFilters = await getFilters(searchParams, router.locale, filtersMapping);
      setFilters(queryFilters);
      setSearchResults(vacancies);
      setResultsPagination(totalResults);
      setSelectedVacancy(vacancies?.[0]);
      setIsVacancyLoading(false);
      setSearchBuckets(buckets);
    };
    load();
  }, [vacancies]);

  useEffect(() => {
    setHomepage(homepage);
  }, [JSON.stringify(homepage)]);

  const seoObject = {
    metaTitle: `${homepage?.title}`,
    ...(homepage?.seo || {}),
  };

  return (
    <>
      <Seo seoAttributes={seoObject} />
      {isInteractive && hasVideos ? (
        <InteractiveHomepage />
      ) : (
        <>
          <StaticHomepage />
          {footerStore.showFooter && <Footer />}
        </>
      )}
    </>
  );
}

export async function getStaticProps({ locale, defaultLocale, draftMode = false }) {
  const preview = draftMode;
  const previewData = draftMode ? { status: "draft" } : {};
  const pageContext = { locale, params: {}, query: {}, preview, previewData };

  const [{ vacancies, numberOfResults, buckets }, { homepage }, { layoutProps }] =
    await Promise.all([
      getVacancies(pageContext, undefined, { limit: SERVER_SIDE_FETCH_LIMIT }),
      getHomepageData(pageContext),
      getLayoutStaticProps(locale, { forceRefresh: draftMode }),
    ]);

  // v5 format: homepage.data is the flat object (single type)
  if (isEmpty(homepage?.data) && locale !== defaultLocale) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      homepage: homepage?.data || null,
      layoutProps,
      totalResults: numberOfResults,
      vacancies,
      buckets,
    },
  };
}

export default observer(Home);
